import { observer } from 'mobx-react-lite'
import type { ReactNode } from 'react'
import type { BusinessInfoRequestedSupportingItemViewModel } from '@/features/jobs/details/tax-type/info-gathering/business-info/BusinessInfoRequestedSupportingItemViewModel'
import type { BusinessProfileVersion } from '@/features/tax-profile/business-profiles/BusinessProfile'
import { useSubject } from '@/hooks/rx-hooks'
import {
  BusinessProfileCardListSection,
  ProvidedBusinessProfileCardListEmptyState,
  OtherProfileDropdown,
  AddNewProfileButton,
  BusinessProfileCardListItem,
  BusinessProfileCardListSectionSkeleton,
} from './index'

/**
 * Props for {@link BusinessProfileCardListSectionsContainer}.
 */
export interface BusinessProfileCardListSectionsContainerProps {
  /**
   * The view model.
   */
  vm: BusinessInfoRequestedSupportingItemViewModel
  /**
   * Whether the entire section is disabled.
   */
  isDisabled?: boolean
}

/**
 * Container for the business profile sections.
 * Orchestrates the display of current and other available business profiles in separate sections.
 */
export const BusinessProfileCardListSectionsContainer = observer(
  function ProvidedBusinessProfileSectionsContainer({
    vm,
    isDisabled,
  }: BusinessProfileCardListSectionsContainerProps) {
    const currentProfile = vm.profile
    const otherProfiles = vm.otherAvailableProfiles

    const addButton = (
      <AddNewProfileButton
        onPress={() => vm.showEditorDialog(null)}
        isDisabled={isDisabled}
      />
    )

    if (vm.isFetchingEditorData) {
      return <BusinessProfileCardListSectionSkeleton />
    }

    return (
      <>
        <BusinessProfileCardListSection
          title="Current"
          action={vm.profile ? addButton : null}
          renderEmptyState={() => (
            <ProvidedBusinessProfileCardListEmptyStateContainer
              vm={vm}
              action={addButton}
            />
          )}
        >
          {currentProfile && (
            <BusinessProfileCardListItemContainer
              profile={currentProfile}
              isCurrent
              onAction={() => vm.showEditorDialog(currentProfile)}
              isDisabled={vm.provideProfile.pending || isDisabled}
            />
          )}
        </BusinessProfileCardListSection>

        {otherProfiles.length > 0 && (
          <BusinessProfileCardListSection
            title={currentProfile ? 'Other' : 'Existing'}
          >
            {otherProfiles.map((profile) => (
              <OtherProfileCardListItemContainer
                key={profile.id}
                profile={profile}
                onSelect={() => vm.provideProfile(profile)}
                onDelete={() => vm.promptDeleteProfilePermanently(profile)}
                isDisabled={vm.provideProfile.pending || isDisabled}
              />
            ))}
          </BusinessProfileCardListSection>
        )}
      </>
    )
  },
)

/**
 * Props for {@link BusinessProfileCardListItemContainer}.
 */
export interface BusinessProfileCardListItemContainerProps {
  /**
   * The business profile.
   */
  profile: BusinessProfileVersion

  /**
   * Callback when the card is clicked.
   */
  onAction?: () => unknown

  /**
   * The action component (e.g., dropdown menu).
   */
  action?: ReactNode

  /**
   * Whether the card is disabled.
   */
  isDisabled?: boolean

  /**
   * Whether the profile is the current one.
   */
  isCurrent?: boolean
}

/**
 * Container that renders the business profile card.
 */
export const BusinessProfileCardListItemContainer = observer(
  function BusinessProfileCardListItemContainer({
    profile,
    onAction,
    action,
    isDisabled,
    isCurrent,
  }: BusinessProfileCardListItemContainerProps) {
    return (
      <BusinessProfileCardListItem
        businessName={profile.businessInfo.businessName}
        ein={profile.businessInfo.employerIdentificationNumber}
        onAction={onAction}
        action={action}
        isDisabled={isDisabled}
        isCurrent={isCurrent}
      />
    )
  },
)

/**
 * Props for {@link OtherProfileCardListItemContainer}.
 */
export interface OtherProfileCardListItemContainerProps {
  /**
   * The business profile.
   */
  profile: BusinessProfileVersion

  /**
   * Callback to select the profile.
   */
  onSelect: () => unknown

  /**
   * Callback to delete the profile.
   */
  onDelete: () => unknown

  /**
   * Whether actions are disabled.
   */
  isDisabled?: boolean
}

/**
 * Container for other profile cards that manages its own dropdown state.
 */
export const OtherProfileCardListItemContainer = observer(
  function OtherProfileCardListItemContainer({
    profile,
    onSelect,
    onDelete,
    isDisabled,
  }: OtherProfileCardListItemContainerProps) {
    const openDropdown$ = useSubject<void>()

    return (
      <BusinessProfileCardListItemContainer
        profile={profile}
        onAction={() => openDropdown$.next()}
        action={
          <OtherProfileDropdown
            open$={openDropdown$}
            onSelect={onSelect}
            onDelete={onDelete}
            isDisabled={isDisabled}
          />
        }
        isDisabled={isDisabled}
      />
    )
  },
)

interface ProvidedBusinessProfileCardListEmptyStateContainerProps {
  vm: BusinessInfoRequestedSupportingItemViewModel
  action: ReactNode
}

/**
 * Provided Business Profile Card List Empty State Container
 */
export const ProvidedBusinessProfileCardListEmptyStateContainer = observer(
  function ProvidedBusinessProfileCardListEmptyStateContainer({
    vm,
    action,
  }: ProvidedBusinessProfileCardListEmptyStateContainerProps) {
    const hasExistingProfiles = vm.otherAvailableProfiles.length > 0
    return (
      <ProvidedBusinessProfileCardListEmptyState
        action={action}
        hasExistingProfiles={hasExistingProfiles}
      />
    )
  },
)
